// Import all command functions
import setDebug from "./commands/setDebug.js";
import noOp from "./commands/noOp.js";
import absolutize from "./commands/absolutize.js";
import jsonataQuery from "./commands/jsonata.js";
import http from "./commands/http.js";
import addCss from "./commands/addCss.js";
import globalCss from "./commands/globalCss.js";
import extract from "./commands/extract.js";
import wrap from "./commands/wrap.js";
import templateJson from "./commands/templateJson.js";
import newLines from "./commands/newLines.js";
import makeTable from "./commands/makeTable.js";
import prepend from "./commands/prepend.js";
import append from "./commands/append.js";
import remove from "./commands/remove.js";
import removeLines from "./commands/removeLines.js";
import wrapLines from "./commands/wrapLines.js";
import setAttribute from "./commands/setAttribute.js";
import csvToJson from "./commands/csvToJson.js";
import htmlTableToJson from "./commands/htmlTableToJson.js";
import raiseEvent from "./commands/raiseEvent.js";
import readFrom from "./commands/readFrom.js";
import writeTo from "./commands/writeTo.js";
import removeBefore from "./commands/removeBefore.js";
import takeBefore from "./commands/takeBefore.js";
import trim from "./commands/trim.js";
import set from "./commands/set.js";
import lowerCase from "./commands/lowerCase.js";
import upperCase from "./commands/upperCase.js";
import capitalize from "./commands/capitalize.js";
import end from "./commands/end.js";
import abort from "./commands/abort.js";
import label from "./commands/label.js";
import gotoCommand from "./commands/goto.js";

import debugTemplate from "./templates/debug.liquid";

import { Liquid } from "liquidjs";
import { WorkingData } from "./WorkingData.js";

//==============================================================================
// PIPELINE CLASS - Main execution engine
//==============================================================================

export class Pipeline {
  //============================================================================
  // PROPERTIES
  //============================================================================

  headCommands = []; // Commands to run before the main, cleared after each run
  commands = []; // Main commands
  tailCommands = []; // Commands to run after the main, cleared after each run

  debug = false;

  vars = new Map(); // Variables for this pipeline execution

  static onLog = null; // Optional callback for log messages: (args) => void
  static onError = null; // Optional callback for error messages: (args) => void
  static globalCommandLib = new Map(); // Commands available to all Pipeline instances
  instanceCommandLib = new Map(); // Commands specific to this instance
  static globalAliasLib = new Map(); // Aliases available to all Pipeline instances
  instanceAliasLib = new Map(); // Aliases specific to this instance

  //============================================================================
  // STATIC INITIALIZATION - Register all commands
  //============================================================================

  static {
    Pipeline.registerGlobalCommand(absolutize);
    Pipeline.registerGlobalCommand(extract);
    Pipeline.registerGlobalCommand(wrap);
    Pipeline.registerGlobalCommand(templateJson);
    Pipeline.registerGlobalCommand(makeTable);
    Pipeline.registerGlobalCommand(newLines);
    Pipeline.registerGlobalCommand(http);
    Pipeline.registerGlobalCommand(addCss);
    Pipeline.registerGlobalCommand(globalCss);
    Pipeline.registerGlobalCommand(jsonataQuery);
    Pipeline.registerGlobalCommand(setDebug);
    Pipeline.registerGlobalCommand(noOp);
    Pipeline.registerGlobalCommand(prepend);
    Pipeline.registerGlobalCommand(append);
    Pipeline.registerGlobalCommand(remove);
    Pipeline.registerGlobalCommand(removeLines);
    Pipeline.registerGlobalCommand(removeBefore);
    Pipeline.registerGlobalCommand(takeBefore);
    Pipeline.registerGlobalCommand(trim);
    Pipeline.registerGlobalCommand(wrapLines);
    Pipeline.registerGlobalCommand(setAttribute);
    Pipeline.registerGlobalCommand(csvToJson);
    Pipeline.registerGlobalCommand(htmlTableToJson);
    Pipeline.registerGlobalCommand(raiseEvent);
    Pipeline.registerGlobalCommand(readFrom);
    Pipeline.registerGlobalCommand(writeTo);
    Pipeline.registerGlobalCommand(set);
    Pipeline.registerGlobalCommand(lowerCase);
    Pipeline.registerGlobalCommand(upperCase);
    Pipeline.registerGlobalCommand(capitalize);
    Pipeline.registerGlobalCommand(end);
    Pipeline.registerGlobalCommand(abort);
    Pipeline.registerGlobalCommand(label);
    Pipeline.registerGlobalCommand(gotoCommand);
  }

  //============================================================================
  // CONSTRUCTOR
  //============================================================================

  constructor(commandSet) {
    if (!commandSet || typeof commandSet !== "object") {
      throw new Error("Pipeline constructor requires a commandSet object");
    }
    if (!Array.isArray(commandSet.commands)) {
      throw new Error(
        "Pipeline constructor requires commandSet.commands to be an array",
      );
    }

    this.commands = commandSet.commands;
    this.debug = this.commands.filter((c) => c.name === "set-debug").length > 0;
    this.commands = this.commands.filter((c) => c.name !== "set-debug");

    // They can add instance commands here
    this.emit("textflow:pipeline-created", { pipeline: this });
  }

  //============================================================================
  // VALIDATION METHODS
  //============================================================================

  /**
   * Validate all commands in the pipeline without executing them
   */
  validate() {
    const errors = [];

    for (let command of this.commands) {
      errors.push(...this.validateCommand(command));
    }

    return errors;
  }

  /**
   * Validate a single command in the pipeline without executing it
   */
  validateCommand(command) {
    const errors = [];

    // Resolve alias to target command with merged arguments
    command = this.resolveAlias(command);

    // Attach hasArg/getArg helpers so validators can inspect arguments by name
    this.wrapCommand(command, this);

    // Check that the command name is registered
    const func = this.getCommandFunction(command.name);
    if (!func) {
      errors.push(`Unknown command: "${command.name}"`);
      return errors;
    }

    // Check each provided argument against the command's arg definitions
    if (!Array.isArray(command.arguments)) {
      errors.push(`Command "${command.name}": arguments must be an array`);
      return errors;
    }

    for (let argument of command.arguments) {
      const argDef =
        func.args && func.args.find((a) => a.name === argument.key);

      // If the arg definition specifies an allowedValues list, the value must appear in it
      if (
        argDef &&
        argDef.allowedValues &&
        !argDef.allowedValues.includes(argument.value)
      ) {
        errors.push(
          `Command "${command.name}", argument "${argument.key}": value "${argument.value}" is not in allowed values ${argDef.allowedValues.join(", ")}`,
        );
      }

      // If the arg definition specifies type "number", the value must be numeric
      if (argDef && argDef.type === "number" && isNaN(Number(argument.value))) {
        errors.push(
          `Command "${command.name}", argument "${argument.key}": value "${argument.value}" is not a valid number`,
        );
      }
    }

    // Run any custom parse validators defined on the command function
    for (let validator of func.parseValidators || []) {
      if (!validator.test) continue; // If there's no test function, skip it
      if (!validator.test(command)) {
        errors.push(`Command "${command.name}": ${validator.message}`);
      }
    }
    return errors;
  }

  //============================================================================
  // COMMAND MANAGEMENT METHODS
  //============================================================================

  /**
   * Get a command function by name, checking instance library first, then static
   */
  getCommandFunction(name) {
    if (this.instanceCommandLib.has(name)) {
      return this.instanceCommandLib.get(name);
    }

    if (Pipeline.globalCommandLib.has(name)) {
      return Pipeline.globalCommandLib.get(name);
    }

    return null;
  }

  /**
   * Add helper methods to a command object
   */
  wrapCommand(command, pipeline) {
    command.pipeline = pipeline;
    command.hasArg = function (name) {
      for (const possibleName of name.split(",").map((n) => n.trim())) {
        if (this.arguments.some((a) => a.key === possibleName)) {
          return true;
        }
      }
      return false;
    };
    command.getArg = function (name) {
      for (const possibleName of name.split(",").map((n) => n.trim())) {
        let value = this.arguments.filter((a) => a.key === possibleName)[0]
          ?.value;

        if (value == null) continue;

        // Is this a variable reference?
        const variableRegex = /^{\w+}$/; // This is a regex because some Liquid templates might start and end with a brace
        if (typeof value === "string" && variableRegex.test(value)) {
          const varName = value.slice(1, -1).trim();
          if (this.pipeline.vars.has(varName)) {
            value = this.pipeline.vars.get(varName);
          } else {
            this.pipeline.log(
              `Warning: Variable "{${varName}}" referenced in command "${this.name}" but not found in pipeline vars`,
            );
            value = null;
          }
        }

        if (value !== null) return value;
      }
      return null;
    };
  }

  /**
   * Get an alias by name, checking instance library first, then static
   */
  getAlias(name) {
    if (this.instanceAliasLib.has(name)) {
      return this.instanceAliasLib.get(name);
    }
    if (Pipeline.globalAliasLib.has(name)) {
      return Pipeline.globalAliasLib.get(name);
    }
    return null;
  }

  /**
   * Expand an alias command into a new command object with merged arguments.
   * Returns the original command if it is not an alias.
   */
  resolveAlias(command) {
    const alias = this.getAlias(command.name);
    if (!alias) return command;

    const presetPairs = Object.entries(alias.presetArgs).map(
      ([key, value]) => ({
        key,
        value,
      }),
    );
    const userArgs = command.arguments ?? [];
    const merged = presetPairs.map((pair) => {
      const override = userArgs.find((a) => a.key === pair.key);
      return override ?? pair;
    });
    const extraUserArgs = userArgs.filter(
      (a) => !Object.prototype.hasOwnProperty.call(alias.presetArgs, a.key),
    );

    return {
      name: alias.target,
      arguments: [...merged, ...extraUserArgs],
      target: command.target,
    };
  }

  /**
   * Register a global command available to all Pipeline instances.
   * Uses the command's own `.key` unless an explicit key is provided.
   */
  static registerGlobalCommand(command, key = command?.key) {
    if (typeof command !== "function") {
      throw new Error("registerGlobalCommand: command must be a function");
    }
    if (!key) {
      throw new Error(
        "registerGlobalCommand: no key provided and command has no .key property",
      );
    }
    Pipeline.globalCommandLib.set(key, command);
  }

  /**
   * Register a command on this pipeline instance only.
   * Uses the command's own `.key` unless an explicit key is provided.
   */
  registerInstanceCommand(command, key = command?.key) {
    if (typeof command !== "function") {
      throw new Error("registerInstanceCommand: command must be a function");
    }
    if (!key) {
      throw new Error(
        "registerInstanceCommand: no key provided and command has no .key property",
      );
    }
    this.instanceCommandLib.set(key, command);
  }

  /**
   * Register a global alias available to all Pipeline instances.
   */
  static registerGlobalAlias(name, target, presetArgs = {}, meta = {}) {
    if (!Pipeline.globalCommandLib.has(target)) {
      throw new Error(
        `registerGlobalAlias: target command "${target}" is not registered`,
      );
    }
    Pipeline.globalAliasLib.set(name, { target, presetArgs, ...meta });
  }

  /**
   * Register an alias on this pipeline instance only.
   */
  registerInstanceAlias(name, target, presetArgs = {}, meta = {}) {
    if (!this.getCommandFunction(target)) {
      throw new Error(
        `registerInstanceAlias: target command "${target}" is not registered`,
      );
    }
    this.instanceAliasLib.set(name, { target, presetArgs, ...meta });
  }

  //============================================================================
  // MAIN EXECUTION METHOD
  //============================================================================

  /**
   * Execute the pipeline on working data
   */
  async execute(working) {
    // If no working data provided, start with empty text
    working = working ?? "";

    // If working is a string, convert it to a WorkingData object
    if (typeof working === "string") {
      working = new WorkingData(working);
    }

    // Check if pipeline execution should start
    if (
      !this.emit(
        "textflow:pipeline-starting",
        { working, pipeline: this },
        true,
      )
    ) {
      this.log(
        "Pipeline cancelled by event listener on textflow:pipeline-starting",
      );
      return this.returnWorking(working);
    }

    const pipelineStart = performance.now();
    const initialInput = working.text;

    // Validate command arrays before execution
    if (
      !Array.isArray(this.headCommands) ||
      !Array.isArray(this.commands) ||
      !Array.isArray(this.tailCommands)
    ) {
      throw new Error(
        "Pipeline execution failed: command arrays are not properly initialized",
      );
    }

    // Execute all commands in order. Using a queue (rather than a for-of)
    // so future commands can manipulate the remaining queue (e.g. jump ahead).
    const queue = [
      ...this.headCommands,
      ...this.commands,
      ...this.tailCommands,
    ];
    while (queue.length > 0) {
      // Are we moving ahead?
      // If working.goto is set, it contains a comma-separated list of labels to try going to.
      // We look for the first label that matches a command in the remaining queue and go to it by removing all commands before it.
      // If no labels match, we ignore the goto.
      if (working.goto) {
        const targets = working.goto.split(",").map((s) => s.trim());
        this.log("Jump targets specified", targets);
        for (let target of targets) {
          const targetIndex = queue.findIndex(
            (cmd) =>
              cmd.name === "label" &&
              cmd.arguments?.find(
                (arg) => arg.key === "name" && arg.value === target,
              ),
          );
          if (targetIndex === -1) {
            this.log(
              `Target "${target}" not found in remaining commands. Ignoring goto.`,
            );
          } else {
            this.log(
              `Going to command "${target}" at index ${targetIndex} in the queue.`,
            );
            queue.splice(0, targetIndex); // Remove commands up to the found target
            break; // Only go to the first valid target in the goto list
          }
        }
        working.goto = null; // Reset goto after using it
      }

      // Get the next command and resolve any aliases
      let command = queue.shift();
      const displayName = command.name;
      command = this.resolveAlias(command);
      this.wrapCommand(command, this);

      this.log(`Executing: ${displayName}`, command.arguments);
      ``;
      // Initialize command history tracking
      const history = {};
      history.command = {
        name: displayName,
        arguments: command.arguments,
      };
      history.input = working.text;

      // Check for unknown commands before execution
      if (!this.getCommandFunction(command.name)) {
        const errorMsg = `Unknown command: "${command.name}". Available commands: ${[
          ...Pipeline.globalCommandLib.keys(),
          ...Pipeline.globalAliasLib.keys(),
        ].join(", ")}`;
        this.log(errorMsg);
        continue; // Skip unknown commands and continue to the next one
      }

      const validateErrors = this.validateCommand(command);
      if (validateErrors.length > 0) {
        this.log(
          `Validation failed for command ${command.name}:`,
          validateErrors,
        );
        working.abort = true;
        return working;
      }

      const func = this.getCommandFunction(command.name);
      if (working.text === null || working.text === undefined) {
        throw new Error(
          `Command "${command.name}" execution failed: working text is null or undefined`,
        );
      }
      const beforeLength = working.text.length;

      // Check if command execution should proceed
      if (
        !this.emit(
          "textflow:command-starting",
          { working, command, pipeline: this },
          true,
        )
      ) {
        this.log(
          "Command cancelled by event listener on textflow:command-starting",
        );
        continue;
      }

      // Execute the command
      let commandResult;
      try {
        const start = performance.now();
        commandResult = await func(working, command, this);
        const duration = performance.now() - start;

        history.duration = duration;

        this.emit("textflow:command-finished", {
          working,
          command,
          pipeline: this,
        });

        history.delta = working.text.length - beforeLength;
        history.output = working.text;
      } catch (ex) {
        const errorMessage = ex instanceof Error ? ex.message : String(ex);
        this.error(`Error in command "${command.name}": ${errorMessage}`);
        if (this.debug && ex instanceof Error && ex.stack) {
          this.error(`Stack trace: ${ex.stack}`);
        }
        working.abort = true;
      }

      // Validate command result type (must be string or null/undefined)
      // This is outside the try-catch so validation errors propagate up
      if (commandResult != null) {
        if (typeof commandResult !== "string") {
          throw new Error(
            `Command "${command.name}" returned ${typeof commandResult} instead of a string`,
          );
        }

        if (command.target) {
          working.writeTo(command.target, commandResult);
        } else {
          working.text = commandResult;
        }
      }

      // Check for abort condition
      if (working.abort) {
        this.log(
          `Abort triggered by command: ${command.name}`,
          working,
          command,
        );
        working.text = undefined; // Clear text on abort to prevent partial results from being used
        return working; // We still return working so that the caller can inspect the history and logs for debugging
      }

      // Check for end condition
      if (working.end) {
        this.log(`End triggered by command: ${command.name}`, working, command);
        queue.length = 0; // Drain the queue so the loop exits and finalization still runs
      }

      this.log(`Executed: ${command.name}`, command.arguments);
      working.history.push(history);
    }

    // Clean up and finalize
    this.tailCommands = []; // Clear tail commands after use

    const pipelineEnd = performance.now();
    working.history.duration = pipelineEnd - pipelineStart;
    working.history.input = initialInput;

    this.emit("textflow:pipeline-finished", { working, pipeline: this });

    return this.returnWorking(working);
  }

  /**
   * Handle the return of working data with final validation
   */
  returnWorking(working) {
    if (
      !this.emit("textflow:returning-data", { working, pipeline: this }, true)
    ) {
      this.log(
        "Return of data cancelled by event listener on textflow:returning-data",
      );
      return new WorkingData();
    }
    return working;
  }

  //============================================================================
  // UTILITY METHODS
  //============================================================================

  /**
   * Emit a custom event when running in a browser
   */
  emit(type, detail, cancelable) {
    try {
      if (typeof document === "undefined") return true; // We're not in a browser environment, so just return true to allow all events

      const event = new CustomEvent(type, {
        detail,
        cancelable: cancelable || false,
      });

      const eventResult = document.dispatchEvent(event);
      return eventResult;
    } catch (error) {
      this.log(
        `Warning: Error during event emission for "${type}": ${error.message}`,
      );
      return true; // Return true to allow pipeline to continue
    }
  }

  /**
   * Log messages if debug mode is enabled
   */
  log(...args) {
    if (this.debug) console.log(...args);
    if (Pipeline.onLog) Pipeline.onLog(args);
  }
  error(...args) {
    if (this.debug) console.error(...args);
    if (Pipeline.onError) Pipeline.onError(args);
  }

  /**
   * Generate debug data for the pipeline execution
   */
  static async getDebugData(working) {
    try {
      if (typeof document === "undefined") {
        throw new Error(
          "Debug data generation requires a browser environment with document object",
        );
      }

      const engine = new Liquid();
      engine.registerFilter("commas", function (value) {
        return Intl.NumberFormat("en-US").format(value);
      });

      const debugHtml = await engine.parseAndRenderSync(debugTemplate, {
        history: working.history,
      });

      const debugElement = document.createElement("debug-data");
      if (!debugElement.attachShadow) {
        throw new Error(
          "Failed to create shadow DOM: element does not support attachShadow",
        );
      }

      const shadowRoot = debugElement.attachShadow({ mode: "open" });
      shadowRoot.innerHTML = debugHtml;
      return debugElement;
    } catch (error) {
      console.error("Debug data generation failed:", error.message);
      throw error;
    }
  }
}
